import { useQuery } from "react-query";
import { useWorkflowsApi } from "@circle-react/api";
import {
  completedCourse,
  completedLesson,
  completedSection,
} from "../components/Events/Course";
import {
  eventEndedForMember,
  eventStartsInAnHour,
  memberAttendedLiveEvent,
  rsvpToEvent,
  rsvpToRecurringEvent,
} from "../components/Events/Event";
import { memberTagged, memberUntagged } from "../components/Events/MemberTag";
import { couponUsedAtCheckout } from "../components/Events/Paywall/couponUsedAtCheckout";
import { couponAdded } from "../components/Events/Paywall/couponAdded";
import { couponRemoved } from "../components/Events/Paywall/couponRemoved";
import { couponEnded } from "../components/Events/Paywall/couponEnded";
import { endedTrialWithoutSubscription } from "../components/Events/Paywall/endedTrialWithoutSubscription";
import { memberInstallmentCompleted } from "../components/Events/Paywall/memberInstallmentCompleted";
import { memberPurchased } from "../components/Events/Paywall/memberPurchased";
import { memberSubscribed } from "../components/Events/Paywall/memberSubscribed";
import { memberSubscriptionResumed } from "../components/Events/Paywall/memberSubscriptionResumed";
import { memberSubscriptionScheduledToCancel } from "../components/Events/Paywall/memberSubscriptionScheduledToCancel";
import { memberTrialConverted } from "../components/Events/Paywall/memberTrialConverted";
import { memberTrialEndedAndNotConverted } from "../components/Events/Paywall/memberTrialEndedAndNotConverted";
import { memberTrialResumed } from "../components/Events/Paywall/memberTrialResumed";
import { memberTrialScheduledToCancel } from "../components/Events/Paywall/memberTrialScheduledToCancel";
import { memberUnsubscribed } from "../components/Events/Paywall/memberUnsubscribed";
import { memberWorkflowsSubscriptionCanceled } from "../components/Events/Paywall/memberWorkflowsSubscriptionCanceled";
import { startedTrial } from "../components/Events/Paywall/startedTrial";
import {
  commentApproved,
  commentCreatedOnPost,
  commentFlagged,
  commentRejected,
  postApproved,
  postFlagged,
  postLiked,
  postPublished,
  postRejected,
} from "../components/Events/PostBasic";
import { memberJoinedSpace, memberLeftSpace } from "../components/Events/Space";
import { eventPublished } from "../components/Events/Space/eventPublished";
import {
  memberJoinedSpaceGroup,
  memberLeftSpaceGroup,
} from "../components/Events/SpaceGroup";

interface FetchPreviewAttributesOptions {
  triggerType: string;
}

interface FetchPreviewAttributesResponse {
  [key: string]: object;
}

const EVENT_RESOURCE = "event";
const SPACE_RESOURCE = "space";
const SPACE_GROUP_RESOURCE = "space_group";
const MEMBER_TAG_RESOURCE = "member_tag";
const POST_RESOURCE = "post";
const FLAGGED_POST_RESOURCES = ["post", "flagged_content"];
const COMMENT_RESOURCE = "comment";
const FLAGGED_COMMENT_RESOURCES = ["comment", "flagged_content"];
const PAYWALL_RESOURCES = [
  "paywall",
  "paywall_price",
  "paywall_coupon",
  "currency",
];

const TRIGGER_TYPE_RESOURCE_MAP = {
  // Event based dynamic vars
  [rsvpToEvent.value]: [EVENT_RESOURCE],
  [rsvpToRecurringEvent.value]: [EVENT_RESOURCE],
  [eventStartsInAnHour.value]: [EVENT_RESOURCE],
  [eventPublished.value]: [EVENT_RESOURCE],
  [eventEndedForMember.value]: [EVENT_RESOURCE],
  [memberAttendedLiveEvent.value]: [EVENT_RESOURCE],

  // Space based dynamic vars
  [memberJoinedSpace.value]: [SPACE_RESOURCE],
  [memberLeftSpace.value]: [SPACE_RESOURCE],
  [completedCourse.value]: [SPACE_RESOURCE],
  [completedLesson.value]: [SPACE_RESOURCE],
  [completedSection.value]: [SPACE_RESOURCE],

  // Space Group based dynamic vars
  [memberJoinedSpaceGroup.value]: [SPACE_GROUP_RESOURCE],
  [memberLeftSpaceGroup.value]: [SPACE_GROUP_RESOURCE],

  // Member based dynamic vars
  [memberTagged.value]: [MEMBER_TAG_RESOURCE],
  [memberUntagged.value]: [MEMBER_TAG_RESOURCE],

  // Post based dynamic vars
  [postPublished.value]: [POST_RESOURCE],
  [postLiked.value]: [POST_RESOURCE],
  [postFlagged.value]: FLAGGED_POST_RESOURCES,
  [postApproved.value]: FLAGGED_POST_RESOURCES,
  [postRejected.value]: FLAGGED_POST_RESOURCES,

  // Comment based dynamic vars
  [commentCreatedOnPost.value]: [COMMENT_RESOURCE],
  [commentFlagged.value]: FLAGGED_COMMENT_RESOURCES,
  [commentApproved.value]: FLAGGED_COMMENT_RESOURCES,
  [commentRejected.value]: FLAGGED_COMMENT_RESOURCES,

  // Paywall based dynamic vars
  [memberPurchased.value]: PAYWALL_RESOURCES,
  [memberSubscribed.value]: PAYWALL_RESOURCES,
  [memberSubscriptionScheduledToCancel.value]: PAYWALL_RESOURCES,
  [memberTrialScheduledToCancel.value]: PAYWALL_RESOURCES,
  [memberUnsubscribed.value]: PAYWALL_RESOURCES,
  [memberWorkflowsSubscriptionCanceled.value]: PAYWALL_RESOURCES,
  [memberSubscriptionResumed.value]: PAYWALL_RESOURCES,
  [memberTrialResumed.value]: PAYWALL_RESOURCES,
  [memberInstallmentCompleted.value]: PAYWALL_RESOURCES,
  [memberTrialEndedAndNotConverted.value]: PAYWALL_RESOURCES,
  [memberTrialConverted.value]: PAYWALL_RESOURCES,
  [startedTrial.value]: PAYWALL_RESOURCES,
  [endedTrialWithoutSubscription.value]: PAYWALL_RESOURCES,
  [couponUsedAtCheckout.value]: PAYWALL_RESOURCES,
  [couponAdded.value]: PAYWALL_RESOURCES,
  [couponRemoved.value]: PAYWALL_RESOURCES,
  [couponEnded.value]: PAYWALL_RESOURCES,
};

export const useFetchPreviewAttributes = ({
  triggerType,
}: FetchPreviewAttributesOptions) => {
  const defaultResourceNames = ["community_member", "community"];
  const triggerResourceName = TRIGGER_TYPE_RESOURCE_MAP[triggerType] || "";
  const resourceNames = [
    ...defaultResourceNames,
    ...triggerResourceName,
  ].filter(Boolean);
  const workflowsApi = useWorkflowsApi();

  return useQuery<FetchPreviewAttributesResponse>(
    ["workflows-fetch-preview-attributes", triggerType],
    () =>
      workflowsApi.fetchPreviewAttributes({
        params: { resource_names: resourceNames },
      }),
  );
};
